<i18n>
{
  "en": {
    "facebookLoginButtonText": "Login With Facebook",
    "facebookLogoutButtonText": "Logout From App",
    "inLoginProgressText": "Please check Facebook Login Popup",
    "inLoginProgressValidatePermissionText": "Please wait...in validating permission progress.",
    "inLoginProgressUpdateUserEmail": "Saving your credential",
    "inLoginProgressNavigateToNext": "next step...",
    "inLoginProgressError": "Some error please wait.."
  },
  "th": {
    "facebookLoginButtonText": "เข้าสู่ระบบด้วย Facebook",
    "facebookLogoutButtonText": "ออกจากระบบแอพ",
    "inLoginProgressText": "กรุณาตรวสอบ Popup การล็อคอินของ Facebook",
    "inLoginProgressValidatePermissionText": "กรุณารอสักครู่เรากำลังตรวจสอบ Facebook Permission ของคุณ",
    "inLoginProgressUpdateUserEmail": "กำลังบันทึกข้อมูลของคุณ",
    "inLoginProgressNavigateToNext": "ขั้นตอนต่อไป..",
    "inLoginProgressError": "พบข้อผิดพลาดบางประการ โปรดรอสักครู่."
  }
}
</i18n>
<template>
  <v-layout
    align-center
    justify-center
    row
    fill-height
  >
    <v-btn
      color="#4267B2"
      dark
      :large="largeButton"
      :x-large="xLargeButton"
      @click="buttonClick"
      v-if="!inLoginProcess"
    >
      <v-icon
        :large="largeIcon"
        left
        class="mr-4"
      >
        fab fa-facebook-square
      </v-icon>
      <v-spacer />
      <span
        :class="{title:largeText}"
        class="font-weight-thin"
      >{{ buttonText }}</span>
    </v-btn>
    <v-alert
      v-if="inLoginProcess"
      type="info"
      class="mt-4 mx-4"
    >
      {{ $t(loginProgressInfo) }}
    </v-alert>
  </v-layout>
</template>

<script>
import firebase from 'firebase/app'
import 'firebase/auth'
import 'firebase/firestore'
import 'firebase/analytics'
import functions from '@/firebaseApp/functions'
import helpers from '@/mixins/helpers'
import { mapState, mapMutations } from 'vuex'
import firebaseUser from '@/mixins/firebaseUser'

export default {
  name: 'PaowFacebookLoginButton',
  mixins: [helpers, firebaseUser],
  props: {
    largeButton: {
      type: Boolean,
      default: false
    },
    largeIcon: {
      type: Boolean,
      default: false
    },
    largeText: {
      type: Boolean,
      default: false
    },
    xLargeButton: {
      type: Boolean,
      default: false
    },
    reRequest: {
      type: Array,
      default: null
    }
  },
  data () {
    return {
      loginProgressInfo: 'inLoginProgressText',
      userEmail: null
    }
  },
  computed: {
    ...mapState({
      user: state => state.user.status,
      facebookToken: state => state.user.facebookToken,
      routerTarget: state => state.user.routerTarget,
      inLoginProcess: state => state.app.inLoginProcess,
      loginError: state => state.errors.login
    }),
    buttonText () {
      return !this.user ? this.$t('facebookLoginButtonText') : this.$t('facebookLogoutButtonText')
    }
  },
  methods: {
    ...mapMutations([
      'user/setRouterTarget',
      'user/setFacebookToken',
      'errors/setLoginError',
      'errors/setPermissionError'
    ]),
    buttonClick () {
      this.$store.dispatch('loginProcess')
      if (!this.user) {
        this.setPersistence()
      } else {
        this.logoutFromApp()
      }
    },
    setPersistence () {
      firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL)
        .then(() => {
          this.loginWithFacebook()
        })
        .catch(error => {
          this.handleLoginError(error)
        })
    },
    loginWithFacebook () {
      const provider = new firebase.auth.FacebookAuthProvider()
      let authType = 'reauthorize'
      provider.addScope('user_friends')
      if (this.reRequest) {
        this.reRequest.forEach(scope => provider.addScope(scope))
        authType = 'rerequest'
      }
      provider.setCustomParameters({
        'display': 'popup',
        'auth_type': authType
      })

      firebase.auth().signInWithPopup(provider)
        .then(loginResult => {
          this['user/setFacebookToken'](loginResult.credential.accessToken)
          this.userEmail = loginResult.user.email
          if (!this.userEmail) {
            this.validateUserFacebookPermission()
          } else {
            this.goToNextPage()
          }
        })
        .catch(error => this.handleLoginError(error))
    },

    validateUserFacebookPermission () {
      this.loginProgressInfo = 'inLoginProgressValidatePermissionText'
      const facebookToken = this.facebookToken
      const permission = ['email', 'user_friends']
      const validateUserFacebookPermission = functions.httpsCallable('validateUserFacebookPermission')
      validateUserFacebookPermission({
        facebookToken,
        permission
      })
        .then(response => {
          if (response.data.error) {
            throw (response.data.error)
          }
          const needPermission = response.data

          if (needPermission.length !== 0) {
            this.needPermission(needPermission)
          } else {
            this.getUserFacebookEmail()
          }
        })
        .catch(error => this.handleLoginError(error))
    },

    needPermission (permission) {
      this['errors/setPermissionError'](permission)
      this.logoutFromApp('permission')
    },

    getUserFacebookEmail () {
      this.loginProgressInfo = 'inLoginProgressUpdateUserEmail'
      const getUserFacebookEmail = functions.httpsCallable('getUserFacebookEmail')
      const facebookToken = this.facebookToken
      getUserFacebookEmail({
        facebookToken
      })
        .then(response => {
          const user = firebase.auth().currentUser
          user.updateEmail(response.data)
            .then(() => this.goToNextPage())
            .catch(error => this.handleLoginError(error))
        })
        .catch(error => this.handleLoginError(error))
    },
    goToNextPage () {
      this.loginProgressInfo = 'inLoginProgressNavigateToNext'
      const user = firebase.auth().currentUser
      this.$store.dispatch('loginSuccess', user)
      this.loginProgressInfo = 'inLoginProgressText'
      firebase.analytics().logEvent('user_login')
      if (this.routerTarget) {
        this.navigateTo(this.routerTarget)
        this['user/setRouterTarget'](null)
      } else {
        this.navigateTo('c-shop')
      }
    },

    handleLoginError (error) {
      this.loginProgressInfo = 'inLoginProgressError'
      let storeError = error
      if (typeof (storeError.message) !== 'string') {
        const errorObj = JSON.parse(error.message)
        storeError = {
          code: errorObj.code,
          message: errorObj.error.message
        }
      }
      this['errors/setLoginError'](storeError)
      this.logoutFromApp('login')
    }
  }
}
</script>

<style scoped>

</style>
